import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, from, of, first, map, switchMap, tap, OperatorFunction } from 'rxjs';
import { PathConstants } from '../../constants/PathConstants';
import { FIREBASE_AUTH, authState } from '../../firebase/firebase.providers';
import { UserProfileService, isProfileComplete } from './user-profile.service';
import { MembershipService } from './membership.service';
import { FeatureFlagService } from '../feature-flag.service';
import { resolveAppEntryPath } from './auth-navigation';
import { needsSignupEmailCode } from './email-verification';
import { isActiveImpersonation } from './firebase-auth.service';
import { reportCaught } from '../../report-error';

const PROFILE_LOAD_FAILED = Symbol('profile-load-failed');

/** Requires a signed-in Firebase user. */
export const authGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);

  return authState(auth).pipe(
    first(),
    map((authStateUser) => !!authStateUser),
    tap((authenticated) => {
      if (!authenticated) {
        router.navigate([PathConstants.LOGIN_PATH]);
      }
    })
  );
};

/** Requires auth + completed onboarding profile. */
export const onboardedGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user);
    })
  );
};

/** Requires auth + onboarding + at least one active clinic membership. */
export const membershipGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user).pipe(
        switchMap((ok) => {
          if (!ok) {
            return of(false);
          }
          return from(memberships.ensureLoaded(user.uid)).pipe(
            map(() => {
              if (memberships.memberships().length === 0) {
                router.navigate([PathConstants.NO_BUSINESS_PATH]);
                return false;
              }
              return true;
            }),
            catchLoadError('[authGuard] Failed to load memberships')
          );
        })
      );
    })
  );
};

/** Requires auth + onboarding while the user has no clinic membership. */
export const noBusinessGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user).pipe(
        switchMap((ok) => {
          if (!ok) {
            return of(false);
          }
          return from(memberships.ensureLoaded(user.uid)).pipe(
            map(() => {
              if (memberships.memberships().length > 0) {
                router.navigate([resolveAppEntryPath(memberships)]);
                return false;
              }
              return true;
            }),
            catchLoadError('[authGuard] Failed to load memberships')
          );
        })
      );
    })
  );
};

/** Requires auth + onboarding + doctor role for the selected business. */
export const doctorGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user).pipe(
        switchMap((ok) => {
          if (!ok) {
            return of(false);
          }
          return from(memberships.ensureLoaded(user.uid)).pipe(
            map(() => {
              if (!memberships.canAccessAppointments()) {
                router.navigate([resolveAppEntryPath(memberships)]);
                return false;
              }
              return true;
            }),
            catchLoadError('[authGuard] Failed to load memberships')
          );
        })
      );
    })
  );
};

/** Blocks admins from patients, medications, agenda, and templates. */
export const clinicalGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user).pipe(
        switchMap((ok) => {
          if (!ok) {
            return of(false);
          }
          return from(memberships.ensureLoaded(user.uid)).pipe(
            map(() => {
              if (!memberships.canManageClinicalRecords()) {
                router.navigate([resolveAppEntryPath(memberships)]);
                return false;
              }
              return true;
            }),
            catchLoadError('[authGuard] Failed to load memberships')
          );
        })
      );
    })
  );
};

/** Requires auth + onboarding + owner or administrador of the selected business. */
export const businessManagerGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user).pipe(
        switchMap((ok) => {
          if (!ok) {
            return of(false);
          }
          return from(memberships.ensureLoaded(user.uid)).pipe(
            map(() => {
              if (!memberships.canManageSelectedBusiness()) {
                router.navigate([resolveAppEntryPath(memberships)]);
                return false;
              }
              return true;
            }),
            catchLoadError('[authGuard] Failed to load memberships')
          );
        })
      );
    })
  );
};

/** Requires the selected clinic to have the invoicing feature flag on. */
export const invoicingGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);
  const flags = inject(FeatureFlagService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user).pipe(
        switchMap((ok) => {
          if (!ok) {
            return of(false);
          }
          return from(memberships.ensureLoaded(user.uid)).pipe(
            map(() => {
              if (!flags.isEnabled('invoicing')) {
                router.navigate([resolveAppEntryPath(memberships)]);
                return false;
              }
              return true;
            }),
            catchLoadError('[authGuard] Failed to load memberships')
          );
        })
      );
    })
  );
};

/** Requires the selected clinic to have teleconsulta on, and a clinical role. */
export const teleconsultaGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);
  const flags = inject(FeatureFlagService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user).pipe(
        switchMap((ok) => {
          if (!ok) {
            return of(false);
          }
          return from(memberships.ensureLoaded(user.uid)).pipe(
            map(() => {
              if (!flags.isEnabled('teleconsulta') || !memberships.canManageClinicalRecords()) {
                router.navigate([resolveAppEntryPath(memberships)]);
                return false;
              }
              return true;
            }),
            catchLoadError('[authGuard] Failed to load memberships')
          );
        })
      );
    })
  );
};

/** Requires the ID token custom claim admin: true. Clinic role admin is not enough. */
export const platformAdminGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return requireCompleteProfile(profiles, router, user).pipe(
        switchMap((ok) => {
          if (!ok) {
            return of(false);
          }
          return from(user.getIdTokenResult()).pipe(
            switchMap((token) => {
              if (token.claims['admin'] === true) {
                return of(true);
              }
              return from(memberships.ensureLoaded(user.uid)).pipe(
                map(() => {
                  router.navigate([resolveAppEntryPath(memberships)]);
                  return false;
                }),
                catchLoadError('[authGuard] Failed to load memberships')
              );
            }),
            catchError((err) => {
              console.error('[authGuard] Failed to read admin claim', err);
              reportCaught(err, 'authGuard.adminClaim');
              return of(false);
            })
          );
        })
      );
    })
  );
};

/** Requires auth; redirects away if onboarding is already done. */
export const onboardingGuard: CanActivateFn = () => {
  const auth = inject(FIREBASE_AUTH);
  const router = inject(Router);
  const profiles = inject(UserProfileService);
  const memberships = inject(MembershipService);

  return authState(auth).pipe(
    first(),
    switchMap((user) => {
      if (!user) {
        router.navigate([PathConstants.LOGIN_PATH]);
        return of(false);
      }
      return from(profiles.ensureLoaded(user.uid)).pipe(
        catchError((err) => {
          console.error('[authGuard] Failed to load profile', err);
          reportCaught(err, 'authGuard.profile');
          return of(PROFILE_LOAD_FAILED);
        }),
        switchMap((profile) => {
          if (profile === PROFILE_LOAD_FAILED) {
            return of(false);
          }
          if (needsSignupEmailCode(user) && !isActiveImpersonation(user.uid)) {
            router.navigate([PathConstants.VERIFY_EMAIL_PATH]);
            return of(false);
          }
          if (!isProfileComplete(profile)) {
            return of(true);
          }
          return from(memberships.ensureLoaded(user.uid)).pipe(
            map(() => {
              router.navigate([resolveAppEntryPath(memberships)]);
              return false;
            }),
            catchLoadError('[authGuard] Failed to load memberships')
          );
        })
      );
    })
  );
};

function requireCompleteProfile(
  profiles: UserProfileService,
  router: Router,
  user: { uid: string; emailVerified: boolean; providerData: { providerId: string }[] }
) {
  return from(profiles.ensureLoaded(user.uid)).pipe(
    catchError((err) => {
      console.error('[authGuard] Failed to load profile', err);
      reportCaught(err, 'authGuard.profile');
      return of(PROFILE_LOAD_FAILED);
    }),
    map((profile) => {
      if (profile === PROFILE_LOAD_FAILED) {
        return false;
      }
      if (needsSignupEmailCode(user) && !isActiveImpersonation(user.uid)) {
        router.navigate([PathConstants.VERIFY_EMAIL_PATH]);
        return false;
      }
      if (!isProfileComplete(profile)) {
        router.navigate([PathConstants.ONBOARDING_PATH]);
        return false;
      }
      return true;
    })
  );
}

function catchLoadError(message: string): OperatorFunction<boolean, boolean> {
  return catchError((err: unknown) => {
    console.error(message, err);
    reportCaught(err, message);
    return of(false);
  });
}
